import './DevPanel_AmpliencePanel.scss';

import React, { useEffect, useState } from 'react';

import FindInPageIcon from '@mui/icons-material/FindInPage';
import LinkIcon from '@mui/icons-material/Link';
import {
  Alert,
  Box,
  CircularProgress,
  Divider,
  Grid,
  Link,
  Paper,
  Stack,
  Typography } from '@mui/material';

import { useAppConfigContext } from '@ulta/core/providers/AppConfigProvider/AppConfigProvider';

import useAmplienceData from '../../hooks/useAmplienceData/useAmplienceData';
import { useDevPanelSocket } from '../../providers/DevPanel_SocketProvider/DevPanel_SocketProvider';
import {
  buildAmplienceLink,
  skipCommonModules
} from '../../utils/amplience/amplience';
import {
  formatDate,
  getUserInfo,
  handleScrollToElement
} from '../../utils/amplienceUtils/amplienceUtils';

const DevPanel_AmpliencePanel = ( { module } ) => {
  const socket = useDevPanelSocket();
  const {
    isLoading,
    error,
    metadata,
    history,
    repo,
    folder,
    users
  } = useAmplienceData( module );

  const [notification, setNotification] = useState( null );

  const { serverRouteUtility, isLocal } = useAppConfigContext();

  const isSkipModule = skipCommonModules.includes( module?.type ) || skipCommonModules.includes( module?.moduleName );

  // Cleanup highlights on unmount
  useEffect( () => {
    return () => {
      const highlightedElements = document.querySelectorAll(
        '.animated-debug-border, .fade-debug-border'
      );
      highlightedElements.forEach( ( el ) => {
        if( el.highlightTimeout ){
          clearTimeout( el.highlightTimeout );
          el.classList.remove( 'animated-debug-border', 'fade-debug-border' );
        }
      } );
    };
  }, [] );

  // Webhook socket listener
  useEffect( () => {
    if( !module?.id || isSkipModule || !socket ){
      return undefined;
    }

    const handleContentUpdated = ( data ) => {
      const isRelated = data?.contentId === module?.id;

      if( isRelated ){
        setNotification( {
          message: `Real-time Update: ${data.message || 'Content updated'}. (Status: ${data.status || 'N/A'})`,
          severity: 'success'
        } );
      }
    };

    socket.on( 'content-updated', handleContentUpdated );

    return () => {
      socket.off( 'content-updated', handleContentUpdated );
    };
  }, [module?.id, isSkipModule, socket] );

  if( !module || !module.id ){
    return (
      <Box p={ 3 }>
        <Alert severity='info'>No module selected or invalid module ID.</Alert>
      </Box>
    );
  }

  if( error && !metadata ){
    return (
      <Box p={ 3 }>
        <Alert severity='error'>{ error }</Alert>
      </Box>
    );
  }

  return (
    <div className='DevPanel_AmpliencePanel'>
      { /* LOADING OVERLAY */ }
      { isLoading && (
        <Box className='loading-overlay'>
          <CircularProgress color='primary' />
          <Typography variant='body1' className='loading-text'>
            Loading...
          </Typography>
        </Box>
      ) }

      { notification && (
        <Box mb={ 2 }>
          <Alert
            severity={ notification.severity }
            onClose={ () => setNotification( null ) }
          >
            { notification.message }
          </Alert>
        </Box>
      ) }

      <Paper className='panel-paper' elevation={ 2 }>
        <Box className='panel-header'>
          <Typography variant='h6' className='panel-title'>
            Module Details { module.id && <>→</> }
          </Typography>
          <Stack spacing={ 2 } direction='row'>
            <Link
              href='#'
              onClick={ ( e ) => {
                e.preventDefault();
                handleScrollToElement( module );
              } }
              rel='noopener'
              underline='hover'
              color='primary'
              className='link-with-icon'
            >
              <FindInPageIcon fontSize='small' />
              Find In Page
            </Link>

            { metadata && (
              <Link
                href={ buildAmplienceLink( module.id, metadata ) }
                target='_blank'
                rel='noopener'
                underline='hover'
                color='primary'
                className='link-with-icon'
              >
                <LinkIcon fontSize='small' />
                Open in Amplience
              </Link>
            ) }
          </Stack>
        </Box>
        <Box className='module-id-box'>
          <strong>Module ID</strong><br />{ module.id }
        </Box>

        <Grid container spacing={ 0 } className='info-grid' wrap='nowrap'>
          <Grid size={ 4 } className='info-column'>
            <Typography variant='subtitle1' className='info-title'>
              Core Info
            </Typography>
            <Box component='ul' className='info-list'>
              <Box component='li' className='info-item'>
                <strong>Type:</strong><br />{ module.type || 'N/A' }
              </Box>
              { metadata?.status && (
                <Box component='li' className='info-item'>
                  <strong>Status:</strong><br />{ metadata.status }
                </Box>
              ) }
              { metadata?.label && (
                <Box component='li' className='info-item'>
                  <strong>Label:</strong><br />{ metadata.label }
                </Box>
              ) }
            </Box>
          </Grid>

          { /* Vertical Divider */ }
          <Divider orientation='vertical' flexItem className='vertical-divider' />

          <Grid size={ 4 } className='info-column bold-title'>
            <Typography variant='subtitle1' className='info-title'>
              Content Info
            </Typography>
            <Box component='ul' className='info-list'>
              { metadata?.createdBy && (
                <Box component='li' className='info-item'>
                  <strong>Created By:</strong><br />{ getUserInfo( users, 'createdBy' ) }
                </Box>
              ) }
              { metadata?.createdDate && (
                <Box component='li' className='info-item'>
                  <strong>Created On:</strong><br />{ formatDate( metadata.createdDate ) }
                </Box>
              ) }
              { folder && (
                <Box component='li' className='info-item'>
                  <strong>Folder:</strong><br />{ folder }
                </Box>
              ) }
              { repo && (
                <Box component='li' className='info-item'>
                  <strong>Content Repo:</strong><br />{ repo }
                </Box>
              ) }
            </Box>
          </Grid>

          { /* Vertical Divider */ }
          <Divider orientation='vertical' flexItem className='vertical-divider' />

          <Grid size={ 4 } className='info-column'>
            <Typography variant='subtitle1' className='info-title'>
              Publishing Info
            </Typography>
            <Box component='ul' className='info-list'>
              { metadata?.lastPublishedDate && (
                <Box component='li' className='info-item'>
                  <strong>Last Published:</strong><br />{ formatDate( metadata.lastPublishedDate ) }
                </Box>
              ) }
              { metadata?.lastPublishedVersion && (
                <Box component='li' className='info-item'>
                  <strong>Version:</strong><br />{ metadata.lastPublishedVersion }
                </Box>
              ) }
              { metadata?.lastModifiedBy && (
                <Box component='li' className='info-item'>
                  <strong>Last Updated By:</strong><br />{ getUserInfo( users, 'lastModifiedBy' ) }
                </Box>
              ) }
              { history?.date && (
                <Box component='li' className='info-item'>
                  <strong>Last Updated:</strong><br />{ formatDate( history.date ) }
                </Box>
              ) }
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </div>
  );
};

export default DevPanel_AmpliencePanel;
